'use client';

import { useEffect, useMemo, useState } from 'react';

import { ChevronLeft, ChevronRight, CircleCheck, Loader2 } from 'lucide-react';

import { type DemoOwnerKey } from '@/lib/demo-routing';
import { useTranslation } from '@/lib/i18n/context';

/* Copy for the post-submit booking step. English is the source of truth.
 * Register: this is functional UI — status lines, errors, aria-labels and a
 * calendar — not hero or sales copy, so the Arabic stays MSA. Open is B2B, and
 * the house ceiling puts colloquial only in the hero question and the CTA
 * button. The two exceptions here are deliberate: the "booked" heading (the
 * emotional peak of the flow) and the "try again" button (a CTA). */
const COPY = {
  en: {
    troublePrefix: 'Trouble booking?',
    openCalendar: 'Open the calendar in a new tab',
    findingTimes: (owner: string | null) =>
      owner ? `Finding times with ${owner}…` : 'Finding times…',
    loadFailed: (owner: string | null) =>
      owner
        ? `We couldn't load the calendar just now — your request is already in, and ${owner} will reach out.`
        : "We couldn't load the calendar just now — your request is already in.",
    tryAgain: 'Try again',
    allSet: (first: string) =>
      first ? `You're all set, ${first}!` : "You're all set!",
    withOwnerPrefix: 'with ',
    ourTeam: 'our team',
    invitePrefix: 'Calendar invite is on its way to ',
    pickTimeWith: 'Pick a time with',
    // Separator between a label and a figure. Latin copy can use a middle dot;
    // Arabic cannot — see the `ar` note below.
    sep: '·',
    minutes: (n: string) => `${n} min`,
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    slotTaken: 'That time was taken just now — pick another.',
    bookFailed: "That didn't go through — please pick a time again.",
    pickADay: 'Pick a highlighted day to see times.',
  },
  ar: {
    troublePrefix: 'تواجه مشكلة في الحجز؟',
    openCalendar: 'افتح التقويم في تبويب جديد',
    findingTimes: (owner: string | null) =>
      owner
        ? `نجهّز لك المواعيد المتاحة مع ${owner}…`
        : 'نجهّز لك المواعيد المتاحة…',
    loadFailed: (owner: string | null) =>
      owner
        ? `ما قدرنا نفتح التقويم الآن — بس طلبك وصلنا، و${owner} يتواصل معك.`
        : 'ما قدرنا نفتح التقويم الآن — بس طلبك وصلنا.',
    tryAgain: 'حاول مرة أخرى',
    allSet: (first: string) => (first ? `تم الحجز يا ${first}.` : 'تم الحجز.'),
    withOwnerPrefix: 'مع ',
    ourTeam: 'فريقنا',
    invitePrefix: 'دعوة التقويم في طريقها إلى ',
    pickTimeWith: 'اختر وقتك مع',
    // NOT '·'. Beside Arabic-Indic digits a middle dot is indistinguishable
    // from ٠ (zero), so "· ٣٠ دقيقة" reads as ٣٠٠ — the wrong meeting length.
    sep: '—',
    minutes: (n: string) => `${n} دقيقة`,
    prevMonth: 'الشهر السابق',
    nextMonth: 'الشهر التالي',
    slotTaken: 'حُجز هذا الموعد قبل قليل — اختر وقتاً آخر.',
    bookFailed: 'لم يكتمل الحجز — اختر وقتاً مرة أخرى.',
    pickADay: 'اختر يوماً من الأيام المتاحة عشان تظهر لك الأوقات.',
  },
};

/* Glyph-only conversion: the value never changes, only the digits used to
 * write it, per the site's Arabic numeral rule. */
const AR_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
const toArabicDigits = (value: number | string) =>
  String(value).replace(/[0-9]/g, (d) => AR_DIGITS[Number(d)]);

export interface SchedulerContact {
  firstName: string;
  lastName: string;
  email: string;
}

/** Everything the post-submit step needs, produced by the form submit. */
export interface SchedulerHandoff {
  contact: SchedulerContact;
  ownerKey: DemoOwnerKey | null;
  ownerFirstName: string | null;
  /** E.164 phone from the form — reused to answer booking-page questions. */
  phone: string;
  /** The routed owner's HubSpot page — safety-net link only. */
  meetingUrl: string | null;
}

interface MeetingSchedulerProps {
  handoff: SchedulerHandoff;
}

interface SlotsResponse {
  slots: number[];
  durationMs: number;
  ownerFirstName: string;
}

type Phase =
  | { kind: 'loading' }
  | { kind: 'pick' }
  | { kind: 'booking'; slot: number }
  | { kind: 'booked'; slot: number }
  | { kind: 'error' };

const MONTHS_SHOWN = 3;

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/**
 * In-house scheduler: a month calendar (next 3 months) of the routed owner's
 * real availability. Pick a day → a row of times → one click books it. No
 * HubSpot iframe, no second form — its required questions are answered
 * server-side from data the visitor already gave us.
 */
const MeetingScheduler = ({ handoff }: MeetingSchedulerProps) => {
  const { locale, isRTL } = useTranslation();
  const isAr = locale === 'ar';
  const c = isAr ? COPY.ar : COPY.en;
  const num = (value: number | string) =>
    isAr ? toArabicDigits(value) : String(value);
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [bookNotice, setBookNotice] = useState<'taken' | 'failed' | null>(null);
  const [slots, setSlots] = useState<number[]>([]);
  const [durationMs, setDurationMs] = useState(1_800_000);
  const [monthOffset, setMonthOffset] = useState(0);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  // Bumped to re-trigger the load effect when the visitor taps "Try again".
  const [loadAttempt, setLoadAttempt] = useState(0);

  const timezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    [],
  );

  useEffect(() => {
    if (!handoff.ownerKey) {
      setPhase({ kind: 'error' });
      return;
    }
    const controller = new AbortController();
    let cancelled = false;
    setPhase({ kind: 'loading' });

    // Client-side retry on top of the server's own retries: even a total
    // outage gets three shots before the visitor ever sees an error.
    const load = async () => {
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const res = await fetch(
            `/api/demo/slots?owner=${handoff.ownerKey}&tz=${encodeURIComponent(timezone)}`,
            { signal: controller.signal },
          );
          if (!res.ok) throw new Error(String(res.status));
          const data = (await res.json()) as SlotsResponse;
          if (data.slots.length === 0) throw new Error('no_slots');
          if (cancelled) return;
          setSlots(data.slots);
          setDurationMs(data.durationMs);
          setSelectedDay(dayKey(new Date(data.slots[0])));
          setMonthOffset(0);
          setPhase({ kind: 'pick' });
          return;
        } catch (err) {
          if ((err as Error)?.name === 'AbortError' || cancelled) return;
          if (attempt < 2) {
            await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
            continue;
          }
          setPhase({ kind: 'error' });
        }
      }
    };
    void load();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [handoff.ownerKey, timezone, loadAttempt]);

  // The browser IS the visitor's timezone, so plain local Date math is safe.
  const slotsByDay = useMemo(() => {
    const map = new Map<string, number[]>();
    for (const slot of slots) {
      const key = dayKey(new Date(slot));
      const list = map.get(key) ?? [];
      list.push(slot);
      map.set(key, list);
    }
    return map;
  }, [slots]);

  // On /ar the calendar reads in Arabic. Deliberately NOT 'ar-SA' — that
  // defaults to the Hijri calendar and would show different dates than the
  // slots we actually hold. '-ca-gregory' pins the calendar and '-nu-arab'
  // pins Arabic-Indic digits, so the month, the day cells and the time chips
  // all use one numeral system. Same dates, same times — only the script and
  // the digit glyphs change.
  const intlLocale = isAr ? 'ar-u-ca-gregory-nu-arab' : undefined;

  const timeFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(intlLocale, {
        hour: 'numeric',
        minute: '2-digit',
      }),
    [intlLocale],
  );
  const monthFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(intlLocale, {
        month: 'long',
        year: 'numeric',
      }),
    [intlLocale],
  );
  const longDayFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(intlLocale, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      }),
    [intlLocale],
  );

  // Weekday header labels, Monday-first.
  const weekdayLabels = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(intlLocale, { weekday: 'narrow' });
    // 2024-01-01 was a Monday.
    return [...Array(7)].map((_, i) => fmt.format(new Date(2024, 0, 1 + i)));
  }, [intlLocale]);

  const now = new Date();
  const viewYear = now.getFullYear();
  const viewMonth = now.getMonth() + monthOffset;
  const monthStart = new Date(viewYear, viewMonth, 1);
  // Monday-first column of the 1st (getDay: 0=Sun).
  const leadingBlanks = (monthStart.getDay() + 6) % 7;
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const selectedSlots = selectedDay ? (slotsByDay.get(selectedDay) ?? []) : [];

  const book = async (slot: number) => {
    if (phase.kind !== 'pick') return;
    setPhase({ kind: 'booking', slot });
    try {
      const res = await fetch('/api/demo/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          owner: handoff.ownerKey,
          startMillisUtc: slot,
          timezone,
          name: `${handoff.contact.firstName} ${handoff.contact.lastName}`.trim(),
          email: handoff.contact.email,
          phone: handoff.phone,
          locale: navigator.language?.toLowerCase() || 'en-us',
        }),
      });
      if (res.status === 409) {
        // Someone grabbed it a second earlier — drop the slot and let the
        // visitor pick again instead of dead-ending.
        setSlots((prev) => prev.filter((sVal) => sVal !== slot));
        setBookNotice('taken');
        setPhase({ kind: 'pick' });
        return;
      }
      if (!res.ok) throw new Error(String(res.status));
      setPhase({ kind: 'booked', slot });
    } catch {
      // Booking failed (transient/validation) — keep the calendar so they can
      // retry a time; the lead is already saved so nothing is lost.
      setBookNotice('failed');
      setPhase({ kind: 'pick' });
    }
  };

  const fallbackLink = handoff.meetingUrl && (
    <p className="text-muted-foreground mt-4 text-center text-xs">
      {c.troublePrefix}{' '}
      <a
        href={handoff.meetingUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="underline hover:text-foreground"
      >
        {c.openCalendar}
      </a>
      .
    </p>
  );

  if (phase.kind === 'loading') {
    return (
      <div className="flex min-h-[24rem] flex-col items-center justify-center gap-3 p-6">
        <Loader2 className="text-muted-foreground size-6 animate-spin" />
        <p className="text-muted-foreground text-sm">
          {c.findingTimes(handoff.ownerFirstName)}
        </p>
      </div>
    );
  }

  if (phase.kind === 'error') {
    return (
      <div className="flex min-h-[24rem] flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-sm">{c.loadFailed(handoff.ownerFirstName)}</p>
        <button
          type="button"
          onClick={() => setLoadAttempt((n) => n + 1)}
          className="inline-flex h-9 items-center justify-center rounded-full bg-neutral-900 px-4 text-sm font-medium text-white transition-colors hover:bg-neutral-700"
        >
          {c.tryAgain}
        </button>
        {fallbackLink}
      </div>
    );
  }

  if (phase.kind === 'booked') {
    const end = phase.slot + durationMs;
    return (
      <div className="flex min-h-[24rem] flex-col items-center justify-center gap-3 p-6 text-center">
        <CircleCheck className="size-12 text-green-600" strokeWidth={1.5} />
        <h3 className="font-serif text-xl text-neutral-900">
          {c.allSet(handoff.contact.firstName)}
        </h3>
        <p className="text-sm text-neutral-600">
          <span className="font-medium text-neutral-900">
            {longDayFmt.format(phase.slot)} {c.sep}{' '}
            <bdi>
              {timeFmt.format(phase.slot)}–{timeFmt.format(end)}
            </bdi>
          </span>{' '}
          {c.withOwnerPrefix}
          <bdi>{handoff.ownerFirstName ?? c.ourTeam}</bdi>
        </p>
        <p className="text-muted-foreground text-xs">
          {/* The email is a Latin run inside Arabic prose — <bdi> isolates it
              so the surrounding RTL text can't reorder its punctuation. */}
          {c.invitePrefix}
          <bdi>{handoff.contact.email}</bdi>.
        </p>
      </div>
    );
  }

  const booking = phase.kind === 'booking';
  const durationMin = Math.round(durationMs / 60000);
  const owner = handoff.ownerFirstName ?? c.ourTeam;

  // In RTL the month header is mirrored, so "previous" sits on the right and
  // has to point that way; the grid itself flows correctly on its own.
  const PrevIcon = isRTL ? ChevronRight : ChevronLeft;
  const NextIcon = isRTL ? ChevronLeft : ChevronRight;

  return (
    <div className="p-1 sm:p-2">
      <div className="mb-3 text-center">
        <p className="text-sm">
          {/* The owner's name is Latin inside Arabic prose and the duration is
              an Arabic-Indic number next to it — both get isolated so the
              middle dot and the digits can't be reordered around them. */}
          {c.pickTimeWith} <bdi className="font-medium">{owner}</bdi>{' '}
          <bdi className="text-muted-foreground">
            {c.sep} {c.minutes(num(durationMin))}
          </bdi>
        </p>
      </div>

      {/* Month header */}
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          aria-label={c.prevMonth}
          disabled={booking || monthOffset === 0}
          onClick={() => setMonthOffset((m) => Math.max(0, m - 1))}
          className="rounded p-1.5 text-neutral-500 hover:bg-neutral-100 disabled:opacity-30"
        >
          <PrevIcon className="size-4" />
        </button>
        <p className="text-sm font-medium">{monthFmt.format(monthStart)}</p>
        <button
          type="button"
          aria-label={c.nextMonth}
          disabled={booking || monthOffset === MONTHS_SHOWN - 1}
          onClick={() =>
            setMonthOffset((m) => Math.min(MONTHS_SHOWN - 1, m + 1))
          }
          className="rounded p-1.5 text-neutral-500 hover:bg-neutral-100 disabled:opacity-30"
        >
          <NextIcon className="size-4" />
        </button>
      </div>

      {/* Month grid */}
      <div className="grid grid-cols-7 gap-y-0.5 text-center">
        {weekdayLabels.map((label, i) => (
          <div
            key={`w${i}`}
            className="text-muted-foreground pb-1 text-[11px] font-medium"
          >
            {label}
          </div>
        ))}
        {[...Array(leadingBlanks)].map((_, i) => (
          <div key={`b${i}`} />
        ))}
        {[...Array(daysInMonth)].map((_, i) => {
          const date = new Date(viewYear, viewMonth, i + 1);
          const key = dayKey(date);
          const hasSlots = slotsByDay.has(key);
          const isSelected = key === selectedDay;
          return (
            <div key={key} className="flex justify-center">
              <button
                type="button"
                disabled={!hasSlots || booking}
                onClick={() => setSelectedDay(key)}
                className={`relative size-9 rounded-full text-sm transition-colors ${
                  isSelected
                    ? 'bg-neutral-900 font-medium text-white'
                    : hasSlots
                      ? 'font-medium text-neutral-900 hover:bg-neutral-100'
                      : 'text-neutral-300'
                }`}
              >
                {num(i + 1)}
                {hasSlots && !isSelected && (
                  <span className="absolute bottom-1 left-1/2 size-1 -translate-x-1/2 rounded-full bg-neutral-900" />
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* Times for the selected day */}
      <div className="mt-3 border-t border-neutral-100 pt-3">
        {bookNotice && (
          <p className="mb-2 text-center text-xs text-amber-600" role="status">
            {bookNotice === 'taken' ? c.slotTaken : c.bookFailed}
          </p>
        )}
        {selectedDay && selectedSlots.length > 0 ? (
          <div className="flex flex-wrap justify-center gap-2">
            {selectedSlots.map((slot) => {
              const isBookingThis = booking && phase.slot === slot;
              return (
                <button
                  key={slot}
                  type="button"
                  disabled={booking}
                  onClick={() => void book(slot)}
                  className="inline-flex h-9 min-w-[5.25rem] items-center justify-center rounded-full border border-neutral-200 px-3 text-sm transition-colors hover:border-neutral-900 disabled:opacity-50"
                >
                  {isBookingThis ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    timeFmt.format(slot)
                  )}
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-muted-foreground text-center text-sm">
            {c.pickADay}
          </p>
        )}
      </div>
    </div>
  );
};

export default MeetingScheduler;
